package com.schoolconnect.app.feature.students

import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.NetworkActivityTracker
import com.schoolconnect.app.datasource.network.response.StudentListResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.repository.student.StudentRepository
import com.schoolconnect.app.repository.subject.SubjectRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.datasource.network.request.StudentUpdateRequest
import com.schoolconnect.app.datasource.network.request.StudentFilterRequest
import com.schoolconnect.app.datasource.network.request.StudentWithSubjectEnrollmentsCreateRequest
import com.schoolconnect.app.feature.common.DebouncedActionRunner
import com.schoolconnect.app.feature.common.AdminBootstrapSequencer
import com.schoolconnect.app.feature.common.AdminPagedListCoordinator
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.feature.common.offset
import com.schoolconnect.app.feature.common.resolveServerPageLoadState
import com.schoolconnect.app.feature.common.resolveServerPageTotal
import com.schoolconnect.app.feature.common.withPageIndex
import com.schoolconnect.app.feature.common.withPageSize
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.Job

class StudentsViewModel {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] StudentsViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val reloadDebouncer = DebouncedActionRunner(scope)
    private val classSectionSearchDebouncer = DebouncedActionRunner(scope)
    private val bootstrapSequencer = AdminBootstrapSequencer(scope, TAG)
    private val pagedListCoordinator = AdminPagedListCoordinator(reloadDebouncer, bootstrapSequencer)
    private var classSectionJob: Job? = null
    private var studentsJob: Job? = null
    private var bootstrapJob: Job? = null
    private var classSectionPickerJob: Job? = null
    private val normalizedSchoolId = UserSessionStore.getHomeRoleSchoolId().trim()
    private val _uiState = MutableStateFlow(
        StudentsUiState(
            schoolId = normalizedSchoolId,
            isLoading = normalizedSchoolId.isNotBlank()
        )
    )

    val uiState: StateFlow<StudentsUiState> = _uiState.asStateFlow()

    init {
        if (normalizedSchoolId.isBlank()) {
            _uiState.update {
                it.copy(
                    errorMessage = "School context was not available for student records."
                )
            }
        } else {
            refreshStudentsData(forceRemote = false)
            loadClassSectionPickerPage(reset = true, forceRemote = false)
        }
    }

    fun onEvent(event: StudentsUiEvent) {
        when (event) {
            StudentsUiEvent.BackClicked -> Unit
            StudentsUiEvent.RefreshRequested -> refreshStudentsData(forceRemote = true)
            is StudentsUiEvent.SearchChanged -> {
                _uiState.update {
                    it.copy(
                        searchQuery = event.query,
                        paging = it.paging.withPageIndex(0),
                        errorMessage = "",
                        infoMessage = ""
                    )
                }
                scheduleStudentsReload()
            }
            is StudentsUiEvent.FiltersApplied -> {
                _uiState.update {
                    it.copy(
                        searchQuery = event.searchQuery,
                        filters = event.values,
                        paging = it.paging.withPageIndex(0),
                        errorMessage = "",
                        infoMessage = ""
                    )
                }
                scope.launch {
                    NetworkActivityTracker.withoutBlockingLoader {
                        refreshStudentsPage(forceRemote = true)
                    }
                }
            }
            StudentsUiEvent.FiltersReset -> {
                _uiState.update {
                    it.copy(
                        searchQuery = "",
                        filters = StudentFilterValues(),
                        paging = it.paging.withPageIndex(0),
                        errorMessage = "",
                        infoMessage = "Filters cleared"
                    )
                }
                refreshStudentsPage(forceRemote = true)
            }
            is StudentsUiEvent.SortChanged -> {
                _uiState.update {
                    val nextAscending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(
                        sortField = event.field,
                        sortAscending = nextAscending,
                        paging = it.paging.withPageIndex(0)
                    )
                }
                refreshStudentsPage(forceRemote = true)
            }
            is StudentsUiEvent.PageChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageIndex(event.pageIndex)) }
                refreshStudentsPage(forceRemote = false)
            }
            is StudentsUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageSize(event.pageSize)) }
                refreshStudentsPage(forceRemote = false)
            }
            is StudentsUiEvent.ClassSectionPickerSearchChanged -> {
                _uiState.update { it.copy(classSectionPicker = it.classSectionPicker.copy(query = event.query, pageIndex = 0, isLoading = true, errorMessage = "")) }
                classSectionSearchDebouncer.submit { loadClassSectionPickerPage(reset = true, forceRemote = true) }
            }
            is StudentsUiEvent.ClassSectionPickerPageChanged -> {
                loadClassSectionPickerPage(reset = event.pageIndex == 0, forceRemote = false)
            }
            StudentsUiEvent.ClassSectionPickerLoadMore -> {
                val picker = _uiState.value.classSectionPicker
                if (!picker.isLoading && !picker.isLoadingMore && picker.hasMore) {
                    loadClassSectionPickerPage(reset = false, forceRemote = false)
                }
            }
            StudentsUiEvent.ClassSectionPickerOpened -> {
                val picker = _uiState.value.classSectionPicker
                if (picker.options.isEmpty() && !picker.isLoading) {
                    loadClassSectionPickerPage(reset = true, forceRemote = true)
                }
            }
            is StudentsUiEvent.SaveStudent -> saveStudent(event.values)
            StudentsUiEvent.StudentFormDismissed -> _uiState.update {
                it.copy(isLoading = false, isSaving = false, apiErrorMessage = "", editForm = null)
            }
            is StudentsUiEvent.EditStudent -> loadStudentForEdit(event.student)
            is StudentsUiEvent.ToggleStatus -> toggleStatus(event.studentId)
            is StudentsUiEvent.DeleteStudent -> deleteStudent(event.studentId)
            StudentsUiEvent.ClearMessages -> _uiState.update {
                it.copy(errorMessage = "", apiErrorMessage = "", infoMessage = "")
            }
        }
    }

    private fun loadClassSectionPickerPage(reset: Boolean, forceRemote: Boolean) {
        classSectionPickerJob?.cancel()
        classSectionPickerJob = scope.launch {
            val picker = _uiState.value.classSectionPicker
            val pageIndex = 0
            _uiState.update {
                it.copy(classSectionPicker = picker.copy(
                    pageIndex = 0,
                    isLoading = reset,
                    isLoadingMore = false,
                    errorMessage = "",
                    options = if (reset) emptyList() else picker.options
                ))
            }
            val result = ClassSectionRepository.loadSchoolClassSectionLookup(
                schoolId = normalizedSchoolId,
                forceRemote = forceRemote && reset && picker.options.isEmpty()
            )
            result.fold(
                onSuccess = { rows ->
                    _uiState.update { current ->
                        val query = picker.query.trim()
                        val mapped = rows.filter { row ->
                            val className = row.className.orEmpty()
                            val sectionName = row.sectionName.orEmpty()
                            val combinedLabel = "$className - $sectionName"
                            query.isBlank() || listOf(className, sectionName, combinedLabel)
                                .any { value -> value.contains(query, ignoreCase = true) }
                        }.mapNotNull { row ->
                            val id = row.id?.trim().orEmpty().ifBlank { return@mapNotNull null }
                            val className = row.className?.trim().orEmpty().ifBlank { return@mapNotNull null }
                            val sectionName = row.sectionName?.trim().orEmpty().ifBlank { return@mapNotNull null }
                            StudentClassSectionOption(id, className, sectionName, row.subjectCodes.orEmpty())
                        }
                        val allClassSectionPairs = rows.mapNotNull { row ->
                            val className = row.className?.trim().orEmpty()
                            val sectionName = row.sectionName?.trim().orEmpty()
                            if (className.isBlank() || sectionName.isBlank()) null else className to sectionName
                        }.distinct()
                        val classes = allClassSectionPairs.map { it.first }
                            .distinct()
                            .sortedWith(compareBySmartText { it })
                        val sectionsByClass = allClassSectionPairs.groupBy({ it.first }, { it.second })
                            .mapValues { (_, sections) -> sections.distinct().sortedWith(compareBySmartText { it }) }
                        val sections = allClassSectionPairs.map { it.second }
                            .distinct()
                            .sortedWith(compareBySmartText { it })
                        current.copy(classSectionPicker = current.classSectionPicker.copy(
                            options = mapped,
                            totalCount = mapped.size,
                            pageIndex = 0,
                            isLoading = false,
                            isLoadingMore = false,
                            hasMore = false
                        ),
                            studentFilterClassOptions = classes,
                            studentFilterSectionOptions = sections,
                            studentFilterSectionsByClass = sectionsByClass
                        )
                    }
                },
                onFailure = { throwable ->
                    _uiState.update { it.copy(classSectionPicker = it.classSectionPicker.copy(isLoading = false, isLoadingMore = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load classes and sections." })) }
                }
            )
        }
    }

    private fun scheduleStudentsReload(immediate: Boolean = false) {
        pagedListCoordinator.schedulePage(immediate = immediate) { pageForceRemote ->
            loadStudentsPage(pageForceRemote)
        }
    }

    private fun refreshStudentsData(forceRemote: Boolean) {
        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }
        pagedListCoordinator.refreshPage(
            forceRemote = forceRemote,
            loadPage = { pageForceRemote -> loadStudentsPage(pageForceRemote) }
        )
    }

    private fun refreshStudentsPage(forceRemote: Boolean) {
        pagedListCoordinator.refreshPage(forceRemote = forceRemote) { pageForceRemote ->
            loadStudentsPage(pageForceRemote)
        }
    }

    private fun loadSubjectFilterOptions(forceRemote: Boolean) {
        scope.launch {
            SubjectRepository.loadSchoolSubjectOptions(
                schoolId = normalizedSchoolId,
                forceRemote = forceRemote
            ).fold(
                onSuccess = { options ->
                    val subjectOptions = options.nameByCode.entries
                        .map { (code, name) -> StudentSubjectOption(code, "$code - $name") }
                        .sortedWith(compareBySmartText { it.label })
                    _uiState.update { it.copy(subjectOptions = subjectOptions) }
                    scLog(
                        "$TAG loadSubjectFilterOptions complete school=$normalizedSchoolId count=${subjectOptions.size}"
                    )
                },
                onFailure = { throwable ->
                    scLog("$TAG loadSubjectFilterOptions failure school=$normalizedSchoolId", throwable)
                }
            )
        }
    }

    // Class-section IDs are only needed for filtering and mutations. Keep this
    // out of the initial roster bootstrap; the roster endpoint already returns
    // the display class and section values.
    private suspend fun ensureClassSectionLabels(forceRemote: Boolean): Boolean {
        if (!forceRemote && _uiState.value.classSectionLabels.isNotEmpty()) return true
        return StudentRepository.loadSchoolClassSectionLabels(
            schoolId = normalizedSchoolId,
            forceRemote = forceRemote
        ).fold(
            onSuccess = { labels ->
                _uiState.update { it.copy(classSectionLabels = labels) }
                true
            },
            onFailure = { throwable ->
                _uiState.update {
                    it.copy(errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load class sections." })
                }
                false
            }
        )
    }

    private suspend fun loadStudentsPage(forceRemote: Boolean) =
        NetworkActivityTracker.withoutBlockingLoader {
            loadStudentsPageInternal(forceRemote)
        }

    private suspend fun loadStudentsPageInternal(forceRemote: Boolean) {
        val currentState = _uiState.value
        val query = currentState.searchQuery.trim()
        val status = currentState.filters.statusCode
        val classSectionId = resolveClassSectionId(currentState.filters, currentState.classSectionLabels)
        val isActive = when (status) {
            SchoolKeys.V_STATUS_ACTIVE -> true
            SchoolKeys.V_STATUS_INACTIVE -> false
            else -> null
        }
        val advancedFilter = StudentFilterRequest(
            searchQuery = query.ifBlank { null },
            // The student filter contract expects the selected class/section
            // values in the POST body. Resolving the class-section UUID here
            // caused an unnecessary academic-year lookup and could send the
            // UUID in classId instead of the selected class name.
            classId = currentState.filters.className.trim().ifBlank { null },
            sectionId = currentState.filters.section.trim().ifBlank { null },
            gender = currentState.filters.genderCode.trim().ifBlank { null },
            status = status.toApiStatusFilter(),
            subjectCodes = currentState.filters.subjectCodes.map { it.trim() }.filter { it.isNotBlank() }.ifEmpty { null },
            bloodGroup = currentState.filters.bloodGroupCode.trim().ifBlank { null },
            createdDateFrom = appDateInputToApi(currentState.filters.createdFrom).orEmpty().ifBlank { null },
            createdDateTo = appDateInputToApi(currentState.filters.createdTo).orEmpty().ifBlank { null }
        )
        val paging = currentState.paging
        val offset = paging.offset()

        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        val result = StudentRepository.loadSchoolStudentsPage(
            schoolId = normalizedSchoolId,
            query = query,
            classSectionId = classSectionId,
            isActive = isActive,
            advancedFilter = advancedFilter,
            pageIndex = paging.pageIndex,
            pageSize = paging.pageSize,
            sort = ServerSort(
                field = currentState.sortField.apiField,
                order = if (currentState.sortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
            ),
            forceRemote = forceRemote
        )

        result.fold(
            onSuccess = { students ->
                val serverTotalCount = students.serverTotalCountOrNull()
                var mappedStudents = students.map { it.toUiRow() }
                updateStudentOptions(students)
                val pageLoad = resolveServerPageLoadState(
                    exactTotal = serverTotalCount,
                    paging = paging,
                    currentItemCount = mappedStudents.size,
                    pageOffset = offset
                )
                if (
                    mappedStudents.isEmpty() &&
                    !forceRemote &&
                    query.isBlank() &&
                    status.isBlank() &&
                    classSectionId == null &&
                    paging.pageIndex == 0 &&
                    pageLoad.paging.totalCount > 0
                ) {
                    scLog(
                        "$TAG loadStudentsPage emptyFirstPage retryWithForceRemote school=$normalizedSchoolId listTotal=${pageLoad.paging.totalCount}"
                    )
                    val retryResult = StudentRepository.loadSchoolStudentsPage(
                        schoolId = normalizedSchoolId,
                        query = query,
                        classSectionId = classSectionId,
                        isActive = isActive,
                        advancedFilter = advancedFilter,
                        pageIndex = paging.pageIndex,
                        pageSize = paging.pageSize,
                        sort = ServerSort(
                            field = currentState.sortField.apiField,
                            order = if (currentState.sortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
                        ),
                        forceRemote = true
                    )
                    retryResult.getOrNull()?.let { retryRows ->
                        mappedStudents = retryRows.map { it.toUiRow() }
                    }
                }
                _uiState.update { current ->
                    current.copy(
                        isLoading = false,
                        students = mappedStudents,
                        paging = pageLoad.paging
                    )
                }
            },
            onFailure = { throwable ->
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load students." }
                    )
                }
            }
        )
    }

    private fun resolveClassSectionId(
        filters: StudentFilterValues,
        classSectionLabels: Map<String, String>
    ): String? {
        val className = filters.className.trim()
        val section = filters.section.trim()
        if (className.isBlank() || section.isBlank()) return null
        val expected = "$className-$section"
        return classSectionLabels.entries.firstOrNull { it.value.equals(expected, ignoreCase = true) }?.key
    }

    private fun String.toApiStatusFilter(): String? = when (trim()) {
        SchoolKeys.V_STATUS_ACTIVE -> "ACTIVE"
        SchoolKeys.V_STATUS_INACTIVE -> "INACTIVE"
        else -> null
    }


    private fun StudentListResponse.toUiRow(): StudentRowUi {
        val className = className.orEmpty().ifBlank { "-" }
        val section = sectionName.orEmpty().ifBlank { "-" }
        val statusCode = if (isActive == true) SchoolKeys.V_STATUS_ACTIVE else SchoolKeys.V_STATUS_INACTIVE
        return StudentRowUi(
            id = id.orEmpty(),
            admissionNo = admissionNumber.orEmpty().ifBlank { "-" },
            studentName = name.orEmpty().ifBlank { "-" },
            className = className,
            section = section,
            rollNo = rollNumber.orEmpty().ifBlank { "-" },
            genderCode = gender.orEmpty(),
            bloodGroupCode = bloodGroup.orEmpty(),
            subjectCodes = subjects.orEmpty()
                .asSequence()
                .filter { it.isActive != false }
                .mapNotNull { it.subjectCode?.trim()?.uppercase()?.takeIf(String::isNotBlank) }
                .distinct()
                .toList(),
            statusCode = statusCode,
            createdDate = createdAt.orEmpty().toDisplayDate(),
            updatedDate = updatedAt.orEmpty().toDisplayDate(),
            createdAtText = createdAt.orEmpty().toDisplayDate(),
            updatedAtText = updatedAt.orEmpty().toDisplayDate(),
            createdBy = createdByName.orEmpty().ifBlank { createdById.orEmpty() }.ifBlank { "-" },
            updatedBy = updatedByName.orEmpty().ifBlank { updatedById.orEmpty() }.ifBlank { "-" },
            primaryGuardian = parents.orEmpty()
                .firstOrNull { it.isActive != false && it.isPrimaryContact == true }
                ?.let { StudentGuardianUi(it.name.orEmpty().ifBlank { "-" }, it.relationshipType.toRelationCode(), it.mobile.orEmpty().ifBlank { "-" }, it.email.orEmpty().ifBlank { "-" }) }
                ?: StudentGuardianUi("-", SchoolKeys.V_REL_FATHER, "-", "-"),
            secondaryGuardian = parents.orEmpty()
                .firstOrNull { it.isActive != false && it.isPrimaryContact != true }
                ?.let { StudentGuardianUi(it.name.orEmpty().ifBlank { "-" }, it.relationshipType.toRelationCode(), it.mobile.orEmpty().ifBlank { "-" }, it.email.orEmpty().ifBlank { "-" }) }
                ?: StudentGuardianUi("-", SchoolKeys.V_REL_MOTHER, "-", "-"),
            dateOfBirth = dob.orEmpty().toDisplayDate()
        )
    }

    private fun String.toDisplayDate(): String {
        val parts = take(10).split("-")
        if (parts.size != 3) return ifBlank { "-" }
        val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
            .getOrNull(parts[1].toIntOrNull()?.minus(1) ?: -1)
            ?: return this
        return "${parts[2]} $month ${parts[0]}"
    }

    private fun String?.toRelationCode(): String = when (this.orEmpty().trim().lowercase()) {
        "father" -> SchoolKeys.V_REL_FATHER
        "mother" -> SchoolKeys.V_REL_MOTHER
        "guardian" -> SchoolKeys.V_REL_GUARDIAN
        else -> this.orEmpty()
    }

    private fun updateStudentOptions(students: List<StudentListResponse>) {
        val sections = students.mapNotNull { student ->
            val className = student.className?.trim().orEmpty()
            val sectionName = student.sectionName?.trim().orEmpty()
            if (className.isBlank() || sectionName.isBlank()) null else StudentClassSectionOption(
                id = "",
                className = className,
                sectionName = sectionName,
                subjectCodes = student.subjects.orEmpty().mapNotNull { subject ->
                    subject.subjectCode?.trim()?.uppercase()?.takeIf(String::isNotBlank)
                }.distinct()
            )
        }.groupBy { it.className to it.sectionName }.map { (key, values) ->
            StudentClassSectionOption(
                id = "",
                className = key.first,
                sectionName = key.second,
                subjectCodes = values.flatMap { it.subjectCodes }.distinct()
            )
        }.sortedWith(
            compareBySmartText<StudentClassSectionOption> { it.className }
                .thenComparator { left, right -> compareBySmartText<String> { it }.compare(left.sectionName, right.sectionName) }
        )
        val subjects = students.flatMap { it.subjects.orEmpty() }
            .filter { it.isActive != false }
            .mapNotNull { subject ->
                val code = subject.subjectCode?.trim()?.uppercase()?.takeIf(String::isNotBlank) ?: return@mapNotNull null
                StudentSubjectOption(code, "$code - ${subject.subjectName?.trim().orEmpty().ifBlank { code }}")
            }.distinctBy { it.code }
            .sortedWith(compareBySmartText { it.label })
        _uiState.update { current ->
            current.copy(
                classSectionOptions = sections,
                subjectOptions = current.subjectOptions.ifEmpty { subjects }
            )
        }
    }

    private fun saveStudent(values: StudentFormValues) {
        if (_uiState.value.isSaving) return
        val validation = validateStudent(values)
        if (listOf(
                validation.admissionNo,
                validation.studentName,
                validation.className,
                validation.section,
                validation.rollNo,
                validation.genderCode,
                validation.dob,
                validation.bloodGroupCode,
                validation.subjectCodes,
                validation.statusCode
            ).any { it.isNotBlank() }
        ) {
            _uiState.update {
                it.copy(errorMessage = "Please fix the highlighted fields.", apiErrorMessage = "", infoMessage = "")
            }
            return
        }

        _uiState.update {
            it.copy(isLoading = true, isSaving = true, errorMessage = "", apiErrorMessage = "", infoMessage = "")
        }
        scope.launch {
            if (!ensureClassSectionLabels(forceRemote = false)) {
                val lookupError = _uiState.value.errorMessage.ifBlank { "Unable to load class sections." }
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        isSaving = false,
                        errorMessage = "",
                        apiErrorMessage = lookupError
                    )
                }
                return@launch
            }
            val classSectionId = values.classSectionId.trim().takeIf { it.isNotBlank() }
                ?: resolveClassSectionId(
                    StudentFilterValues(className = values.className, section = values.section),
                    _uiState.value.classSectionLabels
                )
            if (classSectionId.isNullOrBlank()) {
                _uiState.update { it.copy(isLoading = false, isSaving = false, apiErrorMessage = "The selected class and section are not available.") }
                return@launch
            }
            val links = com.schoolconnect.app.repository.classsubjectlink.ClassSubjectLinkRepository
                .loadClassSectionSubjectLinks(normalizedSchoolId, classSectionId, forceRemote = true)
                .getOrElse { throwable ->
                    _uiState.update { it.copy(isLoading = false, isSaving = false, apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to load class subjects." }) }
                    return@launch
                }
            val subjectCodeMap = SubjectRepository.loadSchoolSubjectShortCodeMap(normalizedSchoolId, forceRemote = false)
                .getOrElse { throwable ->
                    _uiState.update { it.copy(isLoading = false, isSaving = false, apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to load school subjects." }) }
                    return@launch
                }
            val codeToLink = links.mapNotNull { link ->
                val id = link.id?.takeIf { it.isNotBlank() } ?: return@mapNotNull null
                val subjectId = link.subjectId?.takeIf { it.isNotBlank() } ?: return@mapNotNull null
                val code = subjectCodeMap[subjectId]
                code?.uppercase()?.let { it to id }
            }.toMap()
            val linkIds = values.subjectCodes.mapNotNull { codeToLink[it.uppercase()] }.distinct()
            val isActive = true
            val result = if (values.id.isBlank()) {
                StudentRepository.createStudent(
                    StudentWithSubjectEnrollmentsCreateRequest(
                        schoolId = normalizedSchoolId,
                        className = values.className.trim(),
                        sectionName = values.section.trim(),
                        admissionNumber = values.admissionNo.trim(),
                        rollNumber = values.rollNo.trim(),
                        name = values.studentName.trim(),
                        dob = appDateInputToApi(values.dob).orEmpty(),
                        gender = com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("GENDER", values.genderCode),
                        bloodGroup = values.bloodGroupCode.trim().ifBlank { null }?.let {
                            com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("BLOOD_GROUP", it)
                        },
                        isActive = isActive,
                        classSubjectLinkIds = linkIds
                    )
                )
            } else {
                StudentRepository.updateStudent(
                    values.id,
                    StudentUpdateRequest(
                        schoolId = normalizedSchoolId,
                        className = values.className.trim(),
                        sectionName = values.section.trim(),
                        admissionNumber = values.admissionNo.trim(),
                        rollNumber = values.rollNo.trim(),
                        name = values.studentName.trim(),
                        dob = appDateInputToApi(values.dob).orEmpty(),
                        gender = com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("GENDER", values.genderCode),
                        bloodGroup = values.bloodGroupCode.trim().ifBlank { null }?.let {
                            com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("BLOOD_GROUP", it)
                        },
                        isActive = isActive
                    )
                ).fold(
                    onSuccess = { response ->
                        StudentRepository.syncStudentSubjects(normalizedSchoolId, values.id, classSectionId, values.subjectCodes)
                            .map { response }
                    },
                    onFailure = { Result.failure(it) }
                )
            }
            result.fold(
                onSuccess = { response ->
                    StudentRepository.invalidateCaches()
                    AppToastCenter.showSuccess(
                        if (values.id.isBlank()) "Added ${response.name.orEmpty()}" else "Updated ${response.name.orEmpty()}"
                    )
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            editForm = null,
                            errorMessage = "",
                            apiErrorMessage = "",
                            infoMessage = "",
                            mutationSuccessNonce = it.mutationSuccessNonce + 1
                        )
                    }
                    refreshStudentsData(forceRemote = true)
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to save student." }
                        )
                    }
                }
            )
        }
    }

    private fun loadStudentForEdit(row: StudentRowUi) {
        _uiState.update {
            it.copy(
                editForm = studentFormValuesFromRow(row),
                isLoading = true,
                isSaving = false,
                apiErrorMessage = "",
                errorMessage = ""
            )
        }
        scope.launch {
            if (_uiState.value.classSectionLabels.isEmpty()) {
                ensureClassSectionLabels(forceRemote = false)
            }
            val classSectionId = row.className.trim().takeIf { it.isNotBlank() }
                ?.let { className ->
                    _uiState.value.classSectionLabels.entries.firstOrNull { entry ->
                        entry.value.equals("$className-${row.section.trim()}", ignoreCase = true)
                    }?.key
                }
            val subjectResult = if (classSectionId.isNullOrBlank()) {
                Result.failure(IllegalStateException("The selected class and section are not available."))
            } else {
                StudentRepository.loadStudentSubjectCodes(
                    schoolId = normalizedSchoolId,
                    studentId = row.id,
                    classSectionId = classSectionId,
                    forceRemote = true
                )
            }
            subjectResult.fold(
                onSuccess = { subjectCodes ->
                    _uiState.update { state ->
                        state.copy(
                            isLoading = false,
                            editForm = state.editForm?.copy(
                                classSectionId = classSectionId.orEmpty(),
                                subjectCodes = subjectCodes
                            )
                        )
                    }
                },
                onFailure = { throwable ->
                    _uiState.update { state ->
                        state.copy(
                            isLoading = false,
                            apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to load the student's subjects." }
                        )
                    }
                }
            )
        }
    }

    private fun toggleStatus(studentId: String) {
        val student = _uiState.value.students.firstOrNull { it.id == studentId }
        if (student == null) {
            _uiState.update { it.copy(errorMessage = "Student not found.", infoMessage = "") }
            return
        }

        val nextActive = student.statusCode != SchoolKeys.V_STATUS_ACTIVE
        val actionLabel = if (nextActive) "Activated" else "Inactivated"
        _uiState.update {
            it.copy(isLoading = true, isSaving = true, errorMessage = "", apiErrorMessage = "", infoMessage = "")
        }
        scope.launch {
            StudentRepository.toggleStudentStatus(studentId, nextActive).fold(
                onSuccess = {
                    StudentRepository.invalidateCaches()
                    AppToastCenter.showSuccess("$actionLabel ${student.studentName}")
                    _uiState.update {
                        it.copy(isLoading = false, isSaving = false, errorMessage = "", apiErrorMessage = "", infoMessage = "")
                    }
                    refreshStudentsData(forceRemote = true)
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to update student status." },
                            infoMessage = ""
                        )
                    }
                }
            )
        }
    }

    private fun deleteStudent(studentId: String) {
        val student = _uiState.value.students.firstOrNull { it.id == studentId }
        if (student == null) {
            _uiState.update { it.copy(errorMessage = "Student not found.", infoMessage = "") }
            return
        }

        _uiState.update {
            it.copy(isLoading = true, isSaving = true, errorMessage = "", apiErrorMessage = "", infoMessage = "")
        }
        scope.launch {
            StudentRepository.deleteStudent(studentId).fold(
                onSuccess = {
                    AppToastCenter.showSuccess("Deleted ${student.studentName}")
                    _uiState.update {
                        it.copy(isLoading = false, isSaving = false, errorMessage = "", apiErrorMessage = "", infoMessage = "")
                    }
                    refreshStudentsData(forceRemote = true)
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            apiErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to delete student." },
                            infoMessage = ""
                        )
                    }
                }
            )
        }
    }

    fun clear() {
        _uiState.update {
            it.copy(isLoading = false, isSaving = false, apiErrorMessage = "")
        }
        classSectionJob?.cancel()
        studentsJob?.cancel()
        pagedListCoordinator.clear()
        scope.cancel()
    }
}

internal fun validateStudent(values: StudentFormValues): StudentFormErrors {
    return StudentFormErrors(
        admissionNo = if (values.admissionNo.isBlank()) "Admission number is required." else "",
        studentName = if (values.studentName.isBlank()) "Student name is required." else "",
        className = if (values.className.isBlank()) "Class is required." else "",
        section = if (values.section.isBlank()) "Section is required." else "",
        rollNo = if (values.rollNo.isBlank()) "Roll number is required." else "",
        genderCode = if (values.genderCode.isBlank()) "Gender is required." else "",
        dob = if (values.dob.isBlank()) "Date of birth is required." else "",
        bloodGroupCode = if (values.bloodGroupCode.isBlank()) "Blood group is required." else "",
        subjectCodes = if (values.subjectCodes.isEmpty()) "Select at least one subject." else "",
        statusCode = if (values.statusCode.isBlank()) "Status is required." else ""
    )
}
